// src/router-x/index.js 
import React, { Suspense } from 'react';
import { Link, Navigate, Route, Routes, useLocation, matchPath } from 'react-router-dom';
import { useAuth } from '../services/contexts/authContext';
import { Wrapper } from '../components';
import { ProtectedRoutes, URLS } from './routes';
import NotFound from '../app/not-found';
import { Button, Spinner } from '@material-tailwind/react';
import { STATIC_USER_ROLES } from '../utils/enums';
import { getHomePageRedirectUrl } from '../utils/functions';


function ProtectedRoute({ children, title, roles, requiredAllowedPage }) {
  const { user, userDetails, loading, isAuthTransitioning } = useAuth();
  const location = useLocation();


  if (loading || isAuthTransitioning) {
    return <div className='flex flex-col items-center justify-center h-screen'>
      <Spinner />
    </div>
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!userDetails) {
    return <div className='flex flex-col items-center justify-center h-screen'>
      <Spinner />
      We are having trouble connecting to the server. Please try refreshing the page.
    </div>
  }

  if (userDetails?.role === STATIC_USER_ROLES.USER && (location.pathname === URLS.ADMIN.ROOT || location.pathname === URLS.ADMIN.HOME)) {
    return <Navigate to={URLS.CUSTOMER.HOME} replace />;
  }

  if (userDetails?.role === STATIC_USER_ROLES.TEAM_MEMBER && (location.pathname === URLS.ADMIN.ROOT || location.pathname === URLS.ADMIN.HOME)) {
    return <Navigate to={URLS.TEAM_MEMBER.HOME} replace />;
  }

  if (userDetails?.role === STATIC_USER_ROLES.SUPPORT_AGENT && (location.pathname === URLS.ADMIN.ROOT || location.pathname === URLS.ADMIN.HOME)) {
    return <Navigate to={URLS.SUPPORT_AGENT.HOME} replace />;
  }

  if (userDetails?.role === STATIC_USER_ROLES.RECEPTIONIST && (location.pathname === URLS.ADMIN.ROOT || location.pathname === URLS.ADMIN.HOME)) {
    return <Navigate to={URLS.RECEPTIONIST.HOME} replace />;
  }

  // Determine access
  let hasAccess = false;

  // Super admin always has access
  if (userDetails?.role === STATIC_USER_ROLES.SUPER_ADMIN) {
    hasAccess = true;
  }
  // Staff role — check dynamic permissions by finding the matching route's ID
  else if (userDetails?.role === STATIC_USER_ROLES.STAFF) {
    const userPermissions = userDetails?.dynamicRole?.permissions || [];
    // Find the current route definition to get its ID
    const findRouteByPath = (routes, path, parentPath = '') => {
      for (const route of routes) {
        let fullPath = parentPath + route.path;
        fullPath = fullPath.replace(/\/+/g, '/');
        if (matchPath({ path: fullPath, end: true }, path)) return route;
        if (route.nestedRoutes) {
          const nested = findRouteByPath(route.nestedRoutes, path, fullPath);
          if (nested) return nested;
        }
      }
      return null;
    };
    const currentRoute = findRouteByPath(ProtectedRoutes, location.pathname);
    if (currentRoute) {
      hasAccess = userPermissions.includes(currentRoute.id);
      
      // Implicitly grant access to the root/home dashboard for all staff so they aren't stuck in a redirect loop
      if (currentRoute.id === 'FX1' || currentRoute.id === 'FX2') {
        hasAccess = true;
      }
    }
  }
  // Legacy/customer roles — use static permissibleRoles check
  else {
    hasAccess = roles?.includes(userDetails?.role) || false;

    // Check requiredAllowedPage if present
    if (hasAccess && requiredAllowedPage) {
      const userAllowedPages = userDetails?.allowedPages || [];
      hasAccess = userAllowedPages.includes(requiredAllowedPage);
    }
  }

  const redirectUrl = getHomePageRedirectUrl(userDetails?.role);

  if (hasAccess) {
    return <Wrapper title={title}>{children}</Wrapper>;
  }
  else {
    return <div className='flex flex-col items-center justify-center h-screen'>
      <h1 className='text-2xl font-bold'>It seems you are lost, Let's get you back on track</h1>
      <p className='text-sm text-gray-500'>For further assistance, please contact Workbench Administration</p>
      <Link to={redirectUrl}><Button className='mt-4 rounded-full'>Go back on track</Button></Link>
    </div>
  }
}

// Helper function to render nested routes recursively (handles infinite nesting)
// lazy-safe Suspense around page components
const renderNestedRoutes = (routes, parentPath = '') => {
  const renderedRoutes = [];

  routes.forEach((route) => {
    const fullPath = parentPath + route.path;
    const PageComponent = route.page;

    // Add the main route
    renderedRoutes.push(
      <Route
        key={route.id}
        path={fullPath}
        element={
          route.isPrivate ? (
            <ProtectedRoute title={route.name} roles={route.permissibleRoles} requiredAllowedPage={route.requiredAllowedPage}>
              <Suspense fallback={<div className="p-6 text-sm text-gray-500">Loading…</div>}>
                <PageComponent />
              </Suspense>
            </ProtectedRoute>
          ) : (
            <Suspense fallback={<div className="p-6 text-sm text-gray-500">Loading…</div>}>
              <PageComponent />
            </Suspense>
          )
        }
      />
    );

    // Recursively handle nested routes if they exist
    if (route.nestedRoutes && route.nestedRoutes.length > 0) {
      const nestedRoutes = renderNestedRoutes(route.nestedRoutes, fullPath);
      renderedRoutes.push(...nestedRoutes);
    }
  });

  return renderedRoutes;
};

// Functional component
const RouterX = () => {
  return (
    <Routes>
      {renderNestedRoutes(ProtectedRoutes)}
      {/* Plural bookmark/typo → canonical /lead (avoid FX11b perm-id miss for staff) */}
      <Route path={URLS.ADMIN.LEADS} element={<Navigate to={URLS.ADMIN.LEAD} replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};



export default RouterX;